import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
} from 'react-router-dom';
import { lazy } from 'react';
import Stack from '@mui/material/Stack';
import { usePermissions } from '@shared/hooks/use-permissions';
import {
  PERMISSIONS,
  UserRoles,
} from '@features/access-control/dto/permissions';
import { useUserInfo } from '@shared/hooks/use-user-info';
import Sidebar from '@shared/components/sidebar';
import { ErrorBoundary } from 'react-error-boundary';
import { ErrorFallback } from '@shared/components/error-fallback';
import LetterRecommendationView from '@features/secretary/pages/letter-recommendation-view';
import { useMyIp } from '@shared/hooks/use-my-ip';
import RedirectApp from '@features/redirect-app/pages/redirect-app';
import InstallApp from '@features/redirect-app/pages/instalar-app';
import PasswordRecovery from '@features/password-recovery/pages/reset';

const ProtectedRoute = lazy(() => import('./protected-route'));

const LittleWallet = lazy(() => import('@features/public/pages/little-wallet'));
const BackWalletPrint = lazy(
  () => import('@features/public/pages/back-wallet-print'),
);
const FrontWalletPrint = lazy(
  () => import('@features/public/pages/front-wallet-print'),
);

const Login = lazy(() => import('@features/auth/pages/login'));

const ValidateLoginMethod = lazy(
  () => import('@features/auth/pages/validate-login-method'),
);
const VerifyCode = lazy(() => import('@features/auth/pages/verify-code'));

const ResetPassword = lazy(() => import('@features/auth/pages/reset-password'));

const ConfirmAccount = lazy(
  () => import('@features/confirm-account/pages/confirm-account'),
);
const RecoveryAccountCodeMember = lazy(
  () => import('@features/auth/pages/recovery-account-code-member'),
);
const RecoveryAccountCpf = lazy(
  () => import('@features/auth/pages/recovery-account-cpf'),
);
const RecoveryAccountEmail = lazy(
  () => import('@features/auth/pages/recovery-account-email'),
);
const RecoveryAccountPhone = lazy(
  () => import('@features/auth/pages/recovery-account-phone'),
);
const Dashboard = lazy(() => import('@features/dashboard/pages/dashboard'));

const RecoveryAccount = lazy(
  () => import('@features/auth/pages/recovery-account'),
);

const ForgetPassword = lazy(
  () => import('@features/auth/pages/forget-password'),
);

const CompleteRegister = lazy(
  () => import('@features/auth/pages/complete-register'),
);

const Congregations = lazy(
  () => import('@features/churches/pages/congregations'),
);
const AddCongregation = lazy(
  () => import('@features/churches/pages/add-congregation'),
);
const EditCongregation = lazy(
  () => import('@features/churches/pages/edit-congregation'),
);

const Sectors = lazy(() => import('@features/churches/pages/sectors'));
const AddSector = lazy(() => import('@features/churches/pages/add-sector'));
const EditSector = lazy(() => import('@features/churches/pages/edit-sector'));

const Groups = lazy(() => import('@features/jobs/pages/groups'));
const AddGroup = lazy(() => import('@features/jobs/pages/add-group'));
const EditGroup = lazy(() => import('@features/jobs/pages/edit-group'));

const Titles = lazy(() => import('@features/jobs/pages/titles'));
const AddTitle = lazy(() => import('@features/jobs/pages/add-title'));
const EditTitle = lazy(() => import('@features/jobs/pages/edit-title'));

const Positions = lazy(() => import('@features/jobs/pages/positions'));
const AddPosition = lazy(() => import('@features/jobs/pages/add-position'));
const EditPosition = lazy(() => import('@features/jobs/pages/edit-position'));

const Profile = lazy(() => import('@features/profile/pages/profile'));

const Roles = lazy(() => import('@features/access-control/pages/roles'));
const AddRole = lazy(() => import('@features/access-control/pages/add-role'));
const EditRole = lazy(() => import('@features/access-control/pages/edit-role'));

const RequestAccess = lazy(
  () => import('@features/access-control/pages/request-access'),
);
const RequestAccessForm = lazy(
  () => import('@features/access-control/pages/request-access-form'),
);

const AccessRequest = lazy(
  () => import('@features/access-control/pages/access-requests'),
);
const AccessRequestReview = lazy(
  () => import('@features/access-control/pages/access-requests-review'),
);

const ChangeTitleUsers = lazy(
  () => import('@features/jobs/pages/change-title-users'),
);
const ChangeTitle = lazy(() => import('@features/jobs/pages/change-title'));
const ChangeTitleRequests = lazy(
  () => import('@features/jobs/pages/change-title-requests'),
);
const ChangeTitleReview = lazy(
  () => import('@features/jobs/pages/change-title-review'),
);

const Visitors = lazy(() => import('@features/users/pages/visitors'));
const VisitorsDetails = lazy(
  () => import('@features/users/pages/visitors-details'),
);
const AddVisitors = lazy(() => import('@features/users/pages/add-visitors'));

const RequestAffiliation = lazy(
  () => import('@features/users/pages/request-affiliation'),
);
const Members = lazy(() => import('@features/users/pages/members'));
const MembersDetails = lazy(
  () => import('@features/users/pages/members-details'),
);

// MemberGroup
const MemberGroup = lazy(() => import('@features/users/pages/member-group'));
const CreateDraftMemberGroup = lazy(
  () => import('@features/users/pages/create-draft-member-group'),
);
const EditMemberGroupDetails = lazy(
  () => import('@features/users/pages/edit-member-group-details'),
);
const MemberGroupDetails = lazy(
  () => import('@features/users/pages/member-group-details'),
);
const EditMemberGroupGuests = lazy(
  () => import('@features/users/pages/edit-member-group-guests'),
);

const Affiliations = lazy(() => import('@features/users/pages/affiliations'));
const AffiliationsDetails = lazy(
  () => import('@features/users/pages/affiliations-details'),
);
const GroupRequests = lazy(
  () => import('@features/users/pages/group-requests'),
);
const GroupRequestsDetails = lazy(
  () => import('@features/users/pages/group-requests-details'),
);
const MemberGroupNotifications = lazy(
  () => import('@features/users/pages/member-group-notifications'),
);

// Secretary
const DataChange = lazy(() => import('@features/secretary/pages/data-change'));
const DataChangeDetails = lazy(
  () => import('@features/secretary/pages/data-change-details'),
);

const RecoveredAccounts = lazy(
  () => import('@features/secretary/pages/recovered-accounts'),
);
const AccountsToBeVerified = lazy(
  () => import('@features/secretary/pages/accounts-to-be-verified'),
);
const AccountToBeVerifiedDetails = lazy(
  () => import('@features/secretary/pages/accounts-to-be-verified-details'),
);
const AccountsCompare = lazy(
  () => import('@features/secretary/pages/accounts-compare'),
);
const AccountsWithoutPassword = lazy(
  () => import('@features/secretary/pages/accounts-without-password'),
);
const AccountWithoutPasswordDetails = lazy(
  () => import('@features/secretary/pages/accounts-without-password-details'),
);

const RecoverAccountDetails = lazy(
  () => import('@features/secretary/pages/recovered-accounts-details'),
);
const Transfers = lazy(() => import('@features/secretary/pages/transfers'));
const Homologation = lazy(
  () => import('@features/secretary/pages/homologation'),
);

const LetterView = lazy(() => import('@features/secretary/pages/letter-view'));

const Recommendation = lazy(
  () => import('@features/secretary/pages/recommendation'),
);
const RecommendationReview = lazy(
  () => import('@features/secretary/pages/recommendation-review'),
);
const PendingRecommendations = lazy(
  () => import('@features/secretary/pages/pending-recommendations'),
);

const ProfileDetailsAssignAccess = lazy(
  () => import('@features/access-control/pages/profile-details-assign-access'),
);

const AcessProfiles = lazy(
  () => import('@features/access-control/pages/assign-access'),
);

const AccountDeletion = lazy(
  () => import('@features/settings/pages/account-deletion'),
);

const Terms = lazy(() => import('@features/settings/pages/terms'));

const Reasons = lazy(() => import('@features/settings/pages/reasons'));
const AddReason = lazy(() => import('@features/settings/pages/add-reason'));
const EditReason = lazy(() => import('@features/settings/pages/edit-reason'));

const Admins = lazy(() => import('@features/settings/pages/admins'));
const AdminDetails = lazy(
  () => import('@features/settings/pages/admin-details'),
);

const About = lazy(() => import('@features/app/pages/about'));
const Transmissions = lazy(() => import('@features/app/pages/transmissions'));
const TransmissionsDetails = lazy(
  () => import('@features/app/pages/transmissions-details'),
);
const News = lazy(() => import('@features/app/pages/news'));
const AddNews = lazy(() => import('@features/app/pages/add-news'));
const EditNews = lazy(() => import('@features/app/pages/edit-news'));
const Contact = lazy(() => import('@features/app/pages/contact'));
const InternalNotifications = lazy(
  () => import('@features/app/pages/internal-notifications'),
);
const EditInternalNotifications = lazy(
  () => import('@features/app/pages/edit-internal-notifications'),
);
const PushNotifications = lazy(
  () => import('@features/app/pages/push-notifications'),
);
const EditPushNotifications = lazy(
  () => import('@features/app/pages/edit-push-notifications'),
);

const Events = lazy(() => import('@features/events/pages/events'));
const EventsConcluded = lazy(
  () => import('@features/events/pages/events-concluded'),
);

const CreatedEventDetails = lazy(
  () => import('@features/events/pages/created-event-details'),
);
const EditEventDetails = lazy(
  () => import('@features/events/pages/edit-event-details'),
);
const CreateEventDraft = lazy(
  () => import('@features/events/pages/create-draft-event'),
);
const CreateConferenceEvent = lazy(
  () => import('@features/events/pages/create-conference-event'),
);
const CreateConsecrationEvent = lazy(
  () => import('@features/events/pages/create-consecration-event'),
);
const DocumentsReview = lazy(
  () => import('@features/events/pages/documents-review'),
);
const CreateBaptismEvent = lazy(
  () => import('@features/events/pages/create-baptism-event'),
);
const ConcludedEventDetails = lazy(
  () => import('@features/events/pages/concluded-event-details'),
);
const DocumentsView = lazy(
  () => import('@features/events/pages/documents-view'),
);
const CertificatesRequests = lazy(
  () => import('@features/requests/page/certificates-requests'),
);
const CertificatesRequestsDetails = lazy(
  () => import('@features/requests/page/certificates-requests-details'),
);
const WalletRequests = lazy(
  () => import('@features/requests/page/wallet-requests'),
);
const WalletRequestsDetails = lazy(
  () => import('@features/requests/page/wallet-requests-details'),
);

const router = createBrowserRouter([
  {
    path: '/',
    element: <Navigate to="/dashboard" replace />,
  },
  {
    path: '/login',
    element: <Login />,
  },
  {
    path: '/login/validar/:loginToken',
    element: <ValidateLoginMethod />,
  },
  {
    path: '/login/validar/codigo',
    element: <VerifyCode />,
  },
  {
    path: '/esqueci-senha',
    element: <ForgetPassword />,
  },
  {
    path: '/nova-senha',
    element: <ResetPassword />,
  },
  // {
  //   path: '/recuperar-conta/codigo',
  //   element: <RecoveryAccountCodeMember />,
  // },
  {
    path: '/recuperar-conta/cpf',
    element: <RecoveryAccountCpf />,
  },
  {
    path: '/recuperar-conta/email',
    element: <RecoveryAccountEmail />,
  },
  {
    path: '/recuperar-conta/telefone',
    element: <RecoveryAccountPhone />,
  },
  {
    path: '/recuperar-conta',
    element: <RecoveryAccount />,
  },
  {
    path: '/completar-cadastro/:userId',
    element: <CompleteRegister />,
  },
  {
    path: '/editar-conta-recuperada/:accountId',
    element: <CompleteRegister />,
  },
  {
    path: '/confirmar-conta',
    element: <ConfirmAccount />,
  },
  {
    path: '/carta/:letterId',
    element: <LetterView />,
  },
  {
    path: '/recomendacao/:letterId',
    element: <LetterRecommendationView />,
  },
  {
    path: '/carteirinha/:userId',
    element: <LittleWallet />,
  },
  {
    path: '/back-wallet-print/:userId',
    element: <BackWalletPrint />,
  },
  {
    path: '/front-wallet-print/:userId',
    element: <FrontWalletPrint />,
  },
  {
    path: '/dashboard',
    element: (
      <ProtectedRoute>
        <Sidebar>
          <Dashboard />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  // CHURCH MODULE
  {
    path: '/congregacoes',
    element: (
      <ProtectedRoute role={[PERMISSIONS.CONGREGATION_READ]} redirectPath="/">
        <Sidebar>
          <Congregations />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/congregacoes/adicionar',
    element: (
      <ProtectedRoute role={[PERMISSIONS.CONGREGATION_CREATE]} redirectPath="/">
        <Sidebar>
          <AddCongregation />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/congregacoes/:congregationId',
    element: (
      <ProtectedRoute role={[PERMISSIONS.CONGREGATION_UPDATE]} redirectPath="/">
        <Sidebar>
          <EditCongregation />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/setores',
    element: (
      <ProtectedRoute role={[PERMISSIONS.SECTOR_READ]} redirectPath="/">
        <Sidebar>
          <Sectors />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/setores/adicionar',
    element: (
      <ProtectedRoute role={[PERMISSIONS.SECTOR_CREATE]} redirectPath="/">
        <Sidebar>
          <AddSector />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/setores/:sectorId',
    element: (
      <ProtectedRoute role={[PERMISSIONS.SECTOR_UPDATE]} redirectPath="/">
        <Sidebar>
          <EditSector />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  // JOB MODULE
  {
    path: '/titulos',
    element: (
      <ProtectedRoute role={[PERMISSIONS.TITLE_READ]} redirectPath="/">
        <Sidebar>
          <Titles />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/titulos/adicionar',
    element: (
      <ProtectedRoute role={[PERMISSIONS.TITLE_CREATE]} redirectPath="/">
        <Sidebar>
          <AddTitle />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/titulos/:titleId',
    element: (
      <ProtectedRoute role={[PERMISSIONS.TITLE_UPDATE]} redirectPath="/">
        <Sidebar>
          <EditTitle />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/funcoes',
    element: (
      <ProtectedRoute role={[PERMISSIONS.FUNCTION_READ]} redirectPath="/">
        <Sidebar>
          <Positions />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/funcoes/adicionar',
    element: (
      <ProtectedRoute role={[PERMISSIONS.FUNCTION_CREATE]} redirectPath="/">
        <Sidebar>
          <AddPosition />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/funcoes/:id',
    element: (
      <ProtectedRoute role={[PERMISSIONS.FUNCTION_READ]} redirectPath="/">
        <Sidebar>
          <EditPosition />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/grupos',
    element: (
      <ProtectedRoute role={[PERMISSIONS.GROUP_READ]} redirectPath="/">
        <Sidebar>
          <Groups />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/grupos/adicionar',
    element: (
      <ProtectedRoute role={[PERMISSIONS.GROUP_CREATE]} redirectPath="/">
        <Sidebar>
          <AddGroup />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/grupos/:groupId',
    element: (
      <ProtectedRoute role={[PERMISSIONS.GROUP_UPDATE]} redirectPath="/">
        <Sidebar>
          <EditGroup />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/alteracoes',
    element: (
      <ProtectedRoute role={[PERMISSIONS.TITLE_CHANGE_READ]} redirectPath="/">
        <Sidebar>
          <ChangeTitleUsers />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/alteracoes/:userId',
    element: (
      <ProtectedRoute
        role={[PERMISSIONS.TITLE_CHANGE_REQUEST]}
        redirectPath="/"
      >
        <Sidebar>
          <ChangeTitle />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/solicitacoes-titulo',
    element: (
      <ProtectedRoute
        role={[PERMISSIONS.TITLE_CHANGE_PENDING_READ]}
        redirectPath="/"
      >
        <Sidebar>
          <ChangeTitleRequests />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/solicitacoes-titulo/:requestId',
    element: (
      <ProtectedRoute
        role={[PERMISSIONS.TITLE_CHANGE_PENDING_READ]}
        redirectPath="/"
      >
        <Sidebar>
          <ChangeTitleReview />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  // ACCESS CONTROL MODULE
  {
    path: '/perfis',
    element: (
      <ProtectedRoute role={[UserRoles.ADMIN]} redirectPath="/">
        <Sidebar>
          <Roles />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/perfis/adicionar',
    element: (
      <ProtectedRoute role={[UserRoles.ADMIN]} redirectPath="/">
        <Sidebar>
          <AddRole />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/perfis/:roleId',
    element: (
      <ProtectedRoute role={[UserRoles.ADMIN]} redirectPath="/">
        <Sidebar>
          <EditRole />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/atribuir-acesso',
    element: (
      <ProtectedRoute redirectPath="/" role={[UserRoles.ADMIN]}>
        <Sidebar>
          <AcessProfiles />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/atribuir-acesso/:accountId',
    element: (
      <ProtectedRoute redirectPath="/" role={[UserRoles.ADMIN]}>
        <Sidebar>
          <ProfileDetailsAssignAccess />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/solicitar-acesso',
    element: (
      <ProtectedRoute role={[UserRoles.ADMIN]} redirectPath="/">
        <Sidebar>
          <RequestAccess />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/solicitar-acesso/:userId',
    element: (
      <ProtectedRoute role={[UserRoles.ADMIN]} redirectPath="/">
        <Sidebar>
          <RequestAccessForm />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/solicitacoes-acesso',
    element: (
      <ProtectedRoute role={[UserRoles.ADMIN]} redirectPath="/">
        <Sidebar>
          <AccessRequest />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/solicitacoes-acesso/:requestId',
    element: (
      <ProtectedRoute role={[UserRoles.ADMIN]} redirectPath="/">
        <Sidebar>
          <AccessRequestReview />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  //
  {
    path: '/perfil',
    element: (
      <ProtectedRoute>
        <Sidebar>
          <Profile />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  // USER MODULE
  {
    path: '/visitantes',
    element: (
      <ProtectedRoute role={[PERMISSIONS.VISITOR_READ]} redirectPath="/">
        <Sidebar>
          <Visitors />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/visitantes/novo',
    element: (
      <ProtectedRoute role={[PERMISSIONS.VISITOR_CREATE]} redirectPath="/">
        <Sidebar>
          <AddVisitors />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/visitantes/detalhes',
    element: (
      <ProtectedRoute role={[PERMISSIONS.VISITOR_UPDATE]} redirectPath="/">
        <Sidebar>
          <VisitorsDetails />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/visitantes/filiacao/:userId',
    element: (
      <ProtectedRoute redirectPath="/" role={[PERMISSIONS.VISITOR_UPDATE]}>
        <Sidebar>
          <RequestAffiliation />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/membros',
    element: (
      <ProtectedRoute role={[PERMISSIONS.MEMBER_READ]} redirectPath="/">
        <Sidebar>
          <Members />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/membros/detalhes',
    element: (
      <ProtectedRoute role={[PERMISSIONS.MEMBER_UPDATE]} redirectPath="/">
        <Sidebar>
          <MembersDetails />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/grupo-membros',
    element: (
      <ProtectedRoute role={[PERMISSIONS.MEMBER_GROUP_READ]} redirectPath="/">
        <Sidebar>
          <MemberGroup />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/grupo-membros/adicionar',
    element: (
      <ProtectedRoute role={[PERMISSIONS.MEMBER_GROUP_CREATE]} redirectPath="/">
        <Sidebar>
          <CreateDraftMemberGroup />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/grupo-membros/:id/detalhes',
    element: (
      <ProtectedRoute role={[PERMISSIONS.MEMBER_GROUP_READ]} redirectPath="/">
        <Sidebar>
          <MemberGroupDetails />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/grupo-membros/:id/detalhes/editar',
    element: (
      <ProtectedRoute
        role={[
          PERMISSIONS.MEMBER_GROUP_CREATE,
          PERMISSIONS.MEMBER_GROUP_UPDATE,
        ]}
        redirectPath="/"
      >
        <Sidebar>
          <EditMemberGroupDetails />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/grupo-membros/:id/convidados/editar',
    element: (
      <ProtectedRoute
        role={[
          PERMISSIONS.MEMBER_GROUP_CREATE,
          PERMISSIONS.MEMBER_GROUP_UPDATE,
        ]}
        redirectPath="/"
      >
        <Sidebar>
          <EditMemberGroupGuests />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/solicitacoes-grupo',
    element: (
      <ProtectedRoute role={[PERMISSIONS.REQUEST_GROUP_READ]} redirectPath="/">
        <Sidebar>
          <GroupRequests />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/solicitacoes-grupo/:id',
    element: (
      <ProtectedRoute role={[PERMISSIONS.REQUEST_GROUP_READ]} redirectPath="/">
        <Sidebar>
          <GroupRequestsDetails />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/notificacoes-grupo-membros',
    element: (
      <ProtectedRoute
        role={[PERMISSIONS.GROUP_NOTIFICATION_READ]}
        redirectPath="/"
      >
        <Sidebar>
          <MemberGroupNotifications />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/filiacoes',
    element: (
      <ProtectedRoute
        role={[PERMISSIONS.REQUEST_AFFILIATION_READ]}
        redirectPath="/"
      >
        <Sidebar>
          <Affiliations />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/filiacoes/:affiliationId',
    element: (
      <ProtectedRoute
        role={[PERMISSIONS.REQUEST_AFFILIATION_READ]}
        redirectPath="/"
      >
        <Sidebar>
          <AffiliationsDetails />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  // SECRETARY MODULE
  {
    path: '/transferencias',
    element: (
      <ProtectedRoute redirectPath="/" role={[PERMISSIONS.TRANSFERENCE_READ]}>
        <Sidebar>
          <Transfers />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/homologacoes',
    element: (
      <ProtectedRoute redirectPath="/" role={[PERMISSIONS.HOMOLOGATION_READ]}>
        <Sidebar>
          <Homologation />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/solicitar-recomendacao',
    element: (
      <ProtectedRoute redirectPath="/" role={[PERMISSIONS.RECOMMENDATION_READ]}>
        <Sidebar>
          <Recommendation />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/recomendacoes',
    element: (
      <ProtectedRoute
        redirectPath="/"
        role={[PERMISSIONS.TO_SIGN_RECOMMENDATION_READ]}
      >
        <Sidebar>
          <PendingRecommendations />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/recomendacoes/:requestId',
    element: (
      <ProtectedRoute
        redirectPath="/"
        role={[PERMISSIONS.TO_SIGN_RECOMMENDATION_READ]}
      >
        <Sidebar>
          <RecommendationReview />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/alteracoes-pendentes',
    element: (
      <ProtectedRoute role={[PERMISSIONS.DATA_CHANGE_READ]} redirectPath="/">
        <Sidebar>
          <DataChange />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/alteracoes-pendentes/detalhes',
    element: (
      <ProtectedRoute redirectPath="/" role={[PERMISSIONS.DATA_CHANGE_READ]}>
        <Sidebar>
          <DataChangeDetails />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/contas-recuperadas',
    element: (
      <ProtectedRoute
        role={[PERMISSIONS.RECOVERED_ACCOUNTS_READ]}
        redirectPath="/"
      >
        <Sidebar>
          <RecoveredAccounts />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/contas-recuperadas/:recoverAccountId',
    element: (
      <ProtectedRoute
        role={[PERMISSIONS.RECOVERED_ACCOUNTS_READ]}
        redirectPath="/"
      >
        <Sidebar>
          <RecoverAccountDetails />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/contas-a-verificar',
    element: (
      <ProtectedRoute
        redirectPath="/"
        role={[PERMISSIONS.ACCOUNTS_TO_CHECK_READ]}
      >
        <Sidebar>
          <AccountsToBeVerified />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/contas-a-verificar/:accountId',
    element: (
      <ProtectedRoute
        redirectPath="/"
        role={[PERMISSIONS.ACCOUNTS_TO_CHECK_READ]}
      >
        <Sidebar>
          <AccountToBeVerifiedDetails />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/contas-a-verificar/sem-senha',
    element: (
      <ProtectedRoute
        redirectPath="/"
        role={[PERMISSIONS.ACCOUNTS_TO_CHECK_BIND]}
      >
        <Sidebar>
          <AccountsWithoutPassword />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/contas-a-verificar/:recoverAccountId/revisar/:cred8MemberId',
    element: (
      <ProtectedRoute
        redirectPath="/"
        role={[PERMISSIONS.ACCOUNTS_TO_CHECK_BIND]}
      >
        <Sidebar>
          <AccountsCompare />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/contas-sem-senha',
    element: (
      <ProtectedRoute
        redirectPath="/"
        role={[PERMISSIONS.ACCOUNTS_WITHOUT_PASSWORD_READ]}
      >
        <Sidebar>
          <AccountsWithoutPassword />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/contas-sem-senha/detalhes',
    element: (
      <ProtectedRoute
        redirectPath="/"
        role={[PERMISSIONS.ACCOUNTS_WITHOUT_PASSWORD_READ]}
      >
        <Sidebar>
          <AccountWithoutPasswordDetails />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  // EVENT MODULE
  {
    path: '/eventos-abertos',
    element: (
      <ProtectedRoute redirectPath="/" role={[PERMISSIONS.EVENT_DETAILS_READ]}>
        <Sidebar>
          <Events />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/eventos-abertos/adicionar',
    element: (
      <ProtectedRoute
        redirectPath="/"
        role={[PERMISSIONS.EVENT_DETAILS_CREATE]}
      >
        <Sidebar>
          <CreateEventDraft />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/eventos-abertos/:id',
    element: (
      <ProtectedRoute
        redirectPath="/"
        role={[PERMISSIONS.EVENT_DETAILS_CREATE]}
      >
        <Sidebar>
          <CreatedEventDetails />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/eventos-abertos/:id/editar',
    element: (
      <ProtectedRoute
        redirectPath="/"
        role={[
          PERMISSIONS.EVENT_DETAILS_READ,
          PERMISSIONS.EVENT_DETAILS_UPDATE,
        ]}
      >
        <Sidebar>
          <EditEventDetails />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/eventos-abertos/:id/conferencia',
    element: (
      <ProtectedRoute
        redirectPath="/"
        role={[
          PERMISSIONS.EVENT_DETAILS_READ,
          PERMISSIONS.EVENT_DETAILS_UPDATE,
        ]}
      >
        <Sidebar>
          <CreateConferenceEvent />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/eventos-abertos/:id/consagracao',
    element: (
      <ProtectedRoute
        redirectPath="/"
        role={[
          PERMISSIONS.EVENT_DETAILS_READ,
          PERMISSIONS.EVENT_DETAILS_UPDATE,
        ]}
      >
        <Sidebar>
          <CreateConsecrationEvent />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/eventos-abertos/consagracao/documentacao/:approvalId',
    element: (
      <ProtectedRoute
        redirectPath="/"
        role={[
          PERMISSIONS.EVENT_DETAILS_READ,
          PERMISSIONS.EVENT_DETAILS_UPDATE,
        ]}
      >
        <Sidebar>
          <DocumentsReview />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/eventos-abertos/:id/batismo',
    element: (
      <ProtectedRoute
        redirectPath="/"
        role={[
          PERMISSIONS.EVENT_DETAILS_READ,
          PERMISSIONS.EVENT_DETAILS_UPDATE,
        ]}
      >
        <Sidebar>
          <CreateBaptismEvent />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: 'eventos-concluidos',
    element: (
      <ProtectedRoute
        redirectPath="/"
        role={[PERMISSIONS.COMPLETED_EVENTS_READ]}
      >
        <Sidebar>
          <EventsConcluded />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/eventos-concluidos/:id',
    element: (
      <ProtectedRoute
        redirectPath="/"
        role={[PERMISSIONS.COMPLETED_EVENTS_READ]}
      >
        <Sidebar>
          <ConcludedEventDetails />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/eventos-concluidos/consagracao/documentacao/:approvalId',
    element: (
      <ProtectedRoute redirectPath="/" role={[PERMISSIONS.EVENT_DETAILS_READ]}>
        <Sidebar>
          <DocumentsView />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  // SETTING MODULE
  {
    path: '/admins',
    element: (
      <ProtectedRoute redirectPath="/" role={[PERMISSIONS.ADMIN_USERS_READ]}>
        <Sidebar>
          <Admins />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/admins/detalhes',
    element: (
      <ProtectedRoute redirectPath="/" role={[PERMISSIONS.ADMIN_USERS_READ]}>
        <Sidebar>
          <AdminDetails />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/termos',
    element: (
      <ProtectedRoute redirectPath="/" role={[PERMISSIONS.TERMS_OF_USE_READ]}>
        <Sidebar>
          <Terms />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/exclusao-de-contas',
    element: (
      <ProtectedRoute redirectPath="/" role={[UserRoles.SUPERADMIN]}>
        <Sidebar>
          <AccountDeletion />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/motivos',
    element: (
      <ProtectedRoute redirectPath="/" role={[PERMISSIONS.REASONS_READ]}>
        <Sidebar>
          <Reasons />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/motivos/adicionar',
    element: (
      <ProtectedRoute redirectPath="/" role={[PERMISSIONS.REASONS_CREATE]}>
        <Sidebar>
          <AddReason />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/motivos/:reasonId',
    element: (
      <ProtectedRoute redirectPath="/" role={[PERMISSIONS.REASONS_UPDATE]}>
        <Sidebar>
          <EditReason />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  // REQUESTS MODULE
  {
    path: '/certificates-requests',
    element: (
      <ProtectedRoute redirectPath="/" role={[PERMISSIONS.CERTIFICATES_READ]}>
        <Sidebar>
          <CertificatesRequests />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/certificates-requests/:id',
    element: (
      <ProtectedRoute redirectPath="/" role={[PERMISSIONS.CERTIFICATES_READ]}>
        <Sidebar>
          <CertificatesRequestsDetails />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/wallet-requests',
    element: (
      <ProtectedRoute redirectPath="/" role={[PERMISSIONS.PHYSICAL_CARD_READ]}>
        <Sidebar>
          <WalletRequests />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/wallet-requests/:id',
    element: (
      <ProtectedRoute redirectPath="/" role={[PERMISSIONS.CERTIFICATES_READ]}>
        <Sidebar>
          <WalletRequestsDetails />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  // APP MODULE
  {
    path: '/transmissoes',
    element: (
      <ProtectedRoute redirectPath="/" role={[PERMISSIONS.TRANSMISSIONS_READ]}>
        <Sidebar>
          <Transmissions />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/transmissoes/:churchId',
    element: (
      <ProtectedRoute
        redirectPath="/"
        role={[PERMISSIONS.TRANSMISSIONS_UPDATE]}
      >
        <Sidebar>
          <TransmissionsDetails />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/noticias',
    element: (
      <ProtectedRoute redirectPath="/" role={[PERMISSIONS.NEWS_READ]}>
        <Sidebar>
          <News />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/noticias/adicionar',
    element: (
      <ProtectedRoute redirectPath="/" role={[PERMISSIONS.NEWS_CREATE]}>
        <Sidebar>
          <AddNews />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/noticias/:id',
    element: (
      <ProtectedRoute redirectPath="/" role={[PERMISSIONS.NEWS_UPDATE]}>
        <Sidebar>
          <EditNews />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/contato',
    element: (
      <ProtectedRoute redirectPath="/" role={[PERMISSIONS.CONTACT_READ]}>
        <Sidebar>
          <Contact />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/sobre',
    element: (
      <ProtectedRoute redirectPath="/" role={[PERMISSIONS.ABOUT_READ]}>
        <Sidebar>
          <About />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/notificacoes/internas',
    element: (
      <ProtectedRoute
        redirectPath="/"
        role={[PERMISSIONS.INTERNAL_NOTIFICATIONS_READ]}
      >
        <Sidebar>
          <InternalNotifications />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/notificacoes/internas/:id',
    element: (
      <ProtectedRoute
        redirectPath="/"
        role={[PERMISSIONS.INTERNAL_NOTIFICATIONS_UPDATE]}
      >
        <Sidebar>
          <EditInternalNotifications />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/notificacoes/push',
    element: (
      <ProtectedRoute
        redirectPath="/"
        role={[PERMISSIONS.PUSH_NOTIFICATIONS_READ]}
      >
        <Sidebar>
          <PushNotifications />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/notificacoes/push/:id',
    element: (
      <ProtectedRoute
        redirectPath="/"
        role={[PERMISSIONS.PUSH_NOTIFICATIONS_UPDATE]}
      >
        <Sidebar>
          <EditPushNotifications />
        </Sidebar>
      </ProtectedRoute>
    ),
  },
  {
    path: '/redirect-app',
    element: <RedirectApp />,
  },
  {
    path: '/instalar-app',
    element: <InstallApp />,
  },
  {
    path: '/password-recovery',
    element: <PasswordRecovery />,
  },
  // NOT FOUND
  {
    path: '*',
    element: (
      <Stack height="100vh" alignItems="center" justifyContent="center">
        <h2>Página não encontrada</h2>
      </Stack>
    ),
  },
]);

const Routes = () => {
  usePermissions();
  useUserInfo();
  useMyIp();

  return (
    <ErrorBoundary fallbackRender={ErrorFallback}>
      <RouterProvider router={router} />
    </ErrorBoundary>
  );
};

export { Routes };
